import React, { useEffect, useState } from "react";
import { Wallet, Loader2, Info, TrendingUp, TrendingDown, CalendarClock, CalendarCheck } from "lucide-react";
import empApi from "../../lib/empApi";

const rp = (n) => "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");

const EARN_LABELS = { monthly_salary: "Gaji Bulanan", bonus: "Bonus", overtime: "Lembur", incentive: "Insentif" };
const DEDUCT_LABELS = { bpjs_kes: "BPJS Kesehatan", bpjs_tk: "BPJS Ketenagakerjaan", pph21: "PPh 21", loan: "Pinjaman / Kasbon", deduction: "Potongan Lain" };

function Row({ label, value, negative }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-slate-400">{label}</span>
      <span className={`font-semibold ${negative ? "text-red-300" : "text-white"}`}>{negative ? "−" : ""}{rp(value)}</span>
    </div>
  );
}

export default function EmpPayroll() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { empApi.get("/employee/payroll-preview").then(({ data }) => setD(data)).catch(() => setErr(true)); }, []);

  if (err) return <p className="py-10 text-center text-sm text-slate-500" data-testid="emp-payroll-error">Gagal memuat data gaji.</p>;
  if (!d) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-amber-400" /></div>;

  const earnings = d.earnings || {};
  const deductions = d.deductions || {};
  // Only show non-zero optional lines (monthly salary always shown).
  const earnEntries = Object.entries(earnings).filter(([k, v]) => k === "monthly_salary" || Number(v) > 0);
  const deductEntries = Object.entries(deductions).filter(([, v]) => Number(v) > 0);

  return (
    <div className="space-y-4" data-testid="emp-payroll">
      <div>
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-400"><Wallet size={14} /> Payroll</p>
        <h2 className="font-heading text-xl font-bold text-white">Slip Gaji</h2>
        <p className="text-xs text-slate-400">Periode {d.period}</p>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-slate-800 bg-slate-800/40 p-3 text-[11px] text-slate-400" data-testid="emp-payroll-readonly">
        <Info size={14} className="mt-0.5 shrink-0 text-amber-400" />
        Read-only. Gaji dibayar dua tahap: Advance pertengahan bulan & Gaji Final akhir bulan.
      </div>

      {/* Advance + Final hero cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-sky-400/30 bg-gradient-to-br from-sky-400/15 to-blue-500/10 p-4" data-testid="emp-payroll-advance">
          <p className="flex items-center gap-1 text-[11px] font-semibold text-sky-300"><CalendarClock size={13} /> Advance (tgl {d.advance_day})</p>
          <p className="mt-1 font-heading text-2xl font-extrabold text-white">{rp(d.advance_amount)}</p>
        </div>
        <div className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-400/15 to-orange-500/10 p-4" data-testid="emp-payroll-final">
          <p className="flex items-center gap-1 text-[11px] font-semibold text-amber-300"><CalendarCheck size={13} /> Gaji Final (akhir bulan)</p>
          <p className="mt-1 font-heading text-2xl font-extrabold text-white">{rp(d.final_net)}</p>
        </div>
      </div>

      {/* Earnings */}
      <div className="rounded-2xl border border-slate-800 bg-slate-800/40 p-4" data-testid="emp-payroll-earnings">
        <p className="mb-1 flex items-center gap-1.5 text-sm font-bold text-emerald-300"><TrendingUp size={15} /> Pendapatan</p>
        {earnEntries.map(([k, v]) => <Row key={k} label={EARN_LABELS[k] || k} value={v} />)}
        <div className="mt-1 flex items-center justify-between border-t border-slate-700 pt-2 text-sm font-bold">
          <span className="text-slate-300">Total Bruto</span><span className="text-white">{rp(d.gross)}</span>
        </div>
      </div>

      {/* Deductions */}
      {deductEntries.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-800/40 p-4" data-testid="emp-payroll-deductions">
          <p className="mb-1 flex items-center gap-1.5 text-sm font-bold text-red-300"><TrendingDown size={15} /> Potongan</p>
          {deductEntries.map(([k, v]) => <Row key={k} label={DEDUCT_LABELS[k] || k} value={v} negative />)}
          <div className="mt-1 flex items-center justify-between border-t border-slate-700 pt-2 text-sm font-bold">
            <span className="text-slate-300">Total Potongan</span><span className="text-red-300">−{rp(d.total_deductions)}</span>
          </div>
        </div>
      )}

      {/* Net month */}
      <div className="flex items-center justify-between rounded-2xl border border-slate-700 bg-slate-800/60 p-4" data-testid="emp-payroll-net">
        <span className="text-sm font-semibold text-slate-300">Gaji Bersih Bulan Ini</span>
        <span className="font-heading text-xl font-extrabold text-white">{rp(d.net_month)}</span>
      </div>

      {d.note && <p className="px-1 text-[11px] text-slate-500" data-testid="emp-payroll-note">{d.note}</p>}
    </div>
  );
}
